import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { PageContentWrapper, PageHeader, PageDescription } from '../main/LayoutStyles';
import { Card, StyledButton, AlertBox } from '../main/CommonStyles';
import apiClient from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { Save, Loader2, AlertTriangle, UploadCloud, Clock, CheckCircle, Edit, PlusCircle } from 'lucide-react';

const titleSuggestions = ["Psycholog", "Psychoterapeuta", "Psychiatra", "Seksuolog", "Coach"];
const specializationSuggestions = [ "Terapia par", "Terapia indywidualna", "Leczenie lęków", "Depresja", "Uzależnienia", "Zaburzenia odżywiania", "Terapia DDA/DDD", "Wypalenie zawodowe", "Stres", "Rozwój osobisty", "Terapia dzieci i młodzieży" ];
const approachSuggestions = [ "Poznawczo-behawioralny (CBT)", "Psychodynamiczny", "Systemowy", "Humanistyczny", "Gestalt", "Terapia Skoncentrowana na Rozwiązaniach (TSR)", "Terapia schematów", "Terapia akceptacji i zaangażowania (ACT)" ];

const FormGrid = styled.form`
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;
    @media (min-width: 768px) {
        grid-template-columns: 1fr 1fr;
    }
`;

const FormField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    label { font-weight: 600; color: ${({ theme }) => theme.colors.textDark}; }
    input, textarea {
        width: 100%; padding: ${({ theme }) => theme.spacing.sm};
        border: 1px solid ${({ theme }) => theme.colors.borderColor};
        border-radius: ${({ theme }) => theme.borderRadius}; font-size: 0.95rem;
        &:focus {
            outline: none; border-color: ${({ theme }) => theme.colors.primary};
            box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryLightest};
        }
    }
    textarea { min-height: 120px; resize: vertical; }
`;

const FullWidthField = styled(FormField)`
    @media (min-width: 768px) { grid-column: 1 / -1; }
`;

const SuggestionGrid = styled.div`
    display: flex; flex-wrap: wrap; gap: 0.4rem;
`;

const SuggestionChip = styled.button`
    display: inline-flex; align-items: center; gap: 0.25rem;
    font-size: 0.8rem; font-weight: 500; padding: 0.2rem 0.6rem;
    border-radius: 50px; cursor: pointer; transition: all 0.2s ease;
    background-color: ${({ theme, $isActive }) => $isActive ? theme.colors.primary : theme.colors.primaryLightest};
    color: ${({ theme, $isActive }) => $isActive ? theme.colors.white : theme.colors.primaryDark};
    border: 1px solid ${({ theme, $isActive }) => $isActive ? theme.colors.primaryDark : theme.colors.primaryLighter};
    &:hover { transform: translateY(-1px); box-shadow: ${({ theme }) => theme.shadows.sm}; }
`;

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const AvatarUploadSection = styled.div`
    grid-column: 1 / -1; display: flex; flex-direction: column;
    align-items: center; text-align: center; gap: 1.5rem;
    padding: 1rem; border: 1px solid ${({ theme }) => theme.colors.borderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    @media (min-width: 768px) { flex-direction: row; text-align: left; }
`;

const AvatarPreviewContainer = styled.div`
    position: relative; width: 100px; height: 100px;
`;

const AvatarPreview = styled.img`
    width: 100px; height: 100px; border-radius: 50%; object-fit: cover;
    border: 3px solid ${({ theme }) => theme.colors.primaryLightest};
    filter: ${({ $isLoading }) => $isLoading ? 'brightness(0.7)' : 'none'};
    transition: filter 0.2s;
`;

const AvatarLoader = styled.div`
    position: absolute; top: 0; left: 0; right: 0; bottom: 0;
    display: flex; align-items: center; justify-content: center;
    color: white; animation: ${spin} 1s linear infinite;
`;

const ProfileStatusIndicator = styled.div`
    grid-column: 1 / -1; padding: ${({ theme }) => theme.spacing.md};
    border-radius: ${({ theme }) => theme.borderRadius}; font-weight: 600;
    display: flex; align-items: center; gap: ${({ theme }) => theme.spacing.sm};
    background-color: ${({ status, theme }) => status === 'PUBLISHED' ? theme.colors.secondaryLight + '4D' : status === 'PENDING_APPROVAL' ? '#fffbe6' : theme.colors.neutralLight};
    color: ${({ status, theme }) => status === 'PUBLISHED' ? theme.colors.secondaryDark : status === 'PENDING_APPROVAL' ? '#f59e0b' : theme.colors.textMedium};
`;

const CheckboxField = styled.div`
    display: flex; align-items: center; gap: 0.5rem;
    input { width: auto; }
`;

const HiddenFileInput = styled.input` display: none; `;

const FileInputLabel = styled.label`
    cursor: pointer; padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
    background-color: ${({ theme }) => theme.colors.primary}; color: white;
    border-radius: 50px; font-weight: 600; display: inline-flex;
    align-items: center; gap: 0.5rem; transition: background-color 0.2s;
    &:hover { background-color: ${({ theme }) => theme.colors.primaryDark}; }
`;

const PsychologistProfileEditor = () => {
    const { currentUser, isPsychologist, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [profileData, setProfileData] = useState({
        firstName: '', lastName: '', professionalTitle: '',
        bio: '', specializations: '', therapyApproaches: '',
        officeAddress: '', acceptsOnline: false, acceptsInPerson: false,
        websiteUrl: '', contactPhoneNumber: '', status: 'DRAFT',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [previewUrl, setPreviewUrl] = useState(currentUser?.imageUrl || null);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    const fetchProfileData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/profile/me');
            if (response.data) {
                setProfileData({ ...profileData, ...response.data, status: response.data.status || 'DRAFT' });
            }
        } catch (err) {
            if (err.response?.status !== 404) setError('Nie udało się pobrać danych profilu.');
        } finally {
            setLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isPsychologist) fetchProfileData();
        setPreviewUrl(currentUser?.imageUrl || null);
    }, [isPsychologist, fetchProfileData, currentUser]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setProfileData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleChipClick = (field, valueToAdd) => {
        setProfileData(prev => {
            const existingValues = prev[field] ? prev[field].split(',').map(s => s.trim()).filter(Boolean) : [];
            const valueIndex = existingValues.findIndex(v => v.toLowerCase() === valueToAdd.toLowerCase());

            if (valueIndex > -1) {
                existingValues.splice(valueIndex, 1);
            } else {
                existingValues.push(valueToAdd);
            }
            return { ...prev, [field]: existingValues.join(', ') };
        });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setPreviewUrl(URL.createObjectURL(file));
        setIsUploading(true);
        setError('');
        const formData = new FormData();
        formData.append('file', file);
        try {
            await apiClient.post('/profile/me/avatar', { headers: { 'Content-Type': 'multipart/form-data' } });
            setSuccessMessage('Awatar zaktualizowany!');
            await refreshUser();
        } catch (err) {
            setError(err.response?.data?.message || 'Nie udało się przesłać zdjęcia.');
            setPreviewUrl(currentUser?.imageUrl || null);
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true); setError(''); setSuccessMessage('');
        try {
            if (!profileData.firstName || !profileData.lastName || !profileData.professionalTitle || !profileData.bio || !profileData.specializations || !profileData.therapyApproaches) {
                setError("Proszę wypełnić wszystkie wymagane pola oznaczone *.");
                setSaving(false); return;
            }
            const response = await apiClient.put('/profile/me', profileData);
            setProfileData(prev => ({ ...prev, status: response.data.status }));
            setSuccessMessage('Profil został pomyślnie wysłany do weryfikacji!');
            window.scrollTo(0, 0);
        } catch (err) {
            setError(err.response?.data?.details || err.response?.data?.message || 'Wystąpił błąd podczas zapisywania profilu.');
        } finally {
            setSaving(false);
        }
    };

    const getStatusInfo = () => {
        switch (profileData.status) {
            case 'PUBLISHED': return { icon: <CheckCircle />, text: 'Twój profil jest opublikowany.' };
            case 'PENDING_APPROVAL': return { icon: <Clock />, text: 'Twój profil oczekuje na weryfikację.' };
            default: return { icon: <Edit />, text: 'Twój profil jest w trybie roboczym.' };
        }
    };

    const {icon, text} = getStatusInfo();
    if (!isPsychologist) return <PageContentWrapper><PageHeader>Brak dostępu</PageHeader></PageContentWrapper>;
    if (loading) return <PageContentWrapper><div style={{ textAlign: 'center' }}><Loader2 size={48} className="icon-spin" /></div></PageContentWrapper>;

    const renderChips = (field, suggestions) => {
        const selected = profileData[field] ? profileData[field].split(',').map(s => s.trim().toLowerCase()) : [];
        return (
            <SuggestionGrid>
                {suggestions.map(suggestion => (
                    <SuggestionChip key={suggestion} type="button" onClick={() => handleChipClick(field, suggestion)} $isActive={selected.includes(suggestion.toLowerCase())}>
                        <PlusCircle size={12} /> {suggestion}
                    </SuggestionChip>
                ))}
            </SuggestionGrid>
        );
    };

    return (
        <PageContentWrapper className="custom-scrollbar">
            <PageHeader>Edytuj Swój Profil Publiczny</PageHeader>
            <PageDescription>Wypełnij poniższe informacje. Po wysłaniu i zatwierdzeniu, profil stanie się widoczny publicznie.</PageDescription>
            <Card>
                {error && <AlertBox type="error" style={{ marginBottom: '1rem' }}><AlertTriangle />{error}</AlertBox>}
                {successMessage && <AlertBox type="success" style={{ marginBottom: '1rem' }}>{successMessage}</AlertBox>}

                <FormGrid onSubmit={handleSubmit}>
                    <ProfileStatusIndicator status={profileData.status}>{icon} {text}</ProfileStatusIndicator>
                    <AvatarUploadSection>
                        <AvatarPreviewContainer>
                            <AvatarPreview src={previewUrl || ''} alt="Podgląd awatara" $isLoading={isUploading} />
                            {isUploading && <AvatarLoader><Loader2 size={32} /></AvatarLoader>}
                        </AvatarPreviewContainer>
                        <div>
                            <FileInputLabel htmlFor="avatar-upload"><UploadCloud size={18} /> Wybierz zdjęcie...</FileInputLabel>
                            <HiddenFileInput id="avatar-upload" type="file" accept="image/*" onChange={handleFileChange} ref={fileInputRef} disabled={isUploading} />
                            <p style={{fontSize: '0.8rem', color: '#666', marginTop: '0.5rem'}}>Zdjęcie zostanie przesłane automatycznie po wybraniu.</p>
                        </div>
                    </AvatarUploadSection>

                    <FormField>
                        <label htmlFor="firstName">Imię *</label>
                        <input id="firstName" name="firstName" value={profileData.firstName} onChange={handleChange} placeholder="Twoje imię" required />
                    </FormField>
                    <FormField>
                        <label htmlFor="lastName">Nazwisko *</label>
                        <input id="lastName" name="lastName" value={profileData.lastName} onChange={handleChange} placeholder="Twoje nazwisko" required />
                    </FormField>

                    <FullWidthField>
                        <label htmlFor="professionalTitle">Tytuł zawodowy *</label>
                        {renderChips('professionalTitle', titleSuggestions)}
                        <input id="professionalTitle" name="professionalTitle" value={profileData.professionalTitle} onChange={handleChange} placeholder="np. Psycholog, Psychoterapeuta" required />
                    </FullWidthField>

                    <FullWidthField>
                        <label htmlFor="specializations">Specjalizacje *</label>
                        {renderChips('specializations', specializationSuggestions)}
                        <input id="specializations" name="specializations" value={profileData.specializations} onChange={handleChange} placeholder="Wybierz z listy lub wpisz własne, oddzielając przecinkami" required />
                    </FullWidthField>

                    <FullWidthField>
                        <label htmlFor="therapyApproaches">Nurty terapeutyczne *</label>
                        {renderChips('therapyApproaches', approachSuggestions)}
                        <input id="therapyApproaches" name="therapyApproaches" value={profileData.therapyApproaches} onChange={handleChange} placeholder="Wybierz z listy lub wpisz własne, oddzielając przecinkami" required />
                    </FullWidthField>

                    <FullWidthField>
                        <label htmlFor="bio">O mnie (biogram) *</label>
                        <textarea id="bio" name="bio" value={profileData.bio} onChange={handleChange} placeholder="Opisz swoje doświadczenie, podejście do pacjenta..." required></textarea>
                    </FullWidthField>

                    <FormField>
                        <label htmlFor="officeAddress">Adres gabinetu (jeśli dotyczy)</label>
                        <input id="officeAddress" name="officeAddress" value={profileData.officeAddress} onChange={handleChange} placeholder="np. ul. Spokojna 15, 00-123 Warszawa" />
                    </FormField>
                    <FormField>
                        <label htmlFor="contactPhoneNumber">Publiczny nr telefonu (opcjonalnie)</label>
                        <input id="contactPhoneNumber" name="contactPhoneNumber" value={profileData.contactPhoneNumber} onChange={handleChange} placeholder="+48 123 456 789" />
                    </FormField>
                    <FormField>
                        <label>Formy terapii</label>
                        <CheckboxField><input id="acceptsInPerson" name="acceptsInPerson" type="checkbox" checked={profileData.acceptsInPerson} onChange={handleChange} /> <label htmlFor="acceptsInPerson">Wizyty stacjonarne</label></CheckboxField>
                        <CheckboxField><input id="acceptsOnline" name="acceptsOnline" type="checkbox" checked={profileData.acceptsOnline} onChange={handleChange} /> <label htmlFor="acceptsOnline">Terapia online</label></CheckboxField>
                    </FormField>
                    <FormField>
                        <label htmlFor="websiteUrl">Link do strony WWW (opcjonalnie)</label>
                        <input id="websiteUrl" name="websiteUrl" type="url" value={profileData.websiteUrl} onChange={handleChange} placeholder="https://moj-gabinet.pl" />
                    </FormField>

                    <FullWidthField>
                        {profileData.status !== 'PENDING_APPROVAL' && (
                            <StyledButton type="submit" disabled={saving || isUploading} style={{ justifySelf: 'start', marginTop: '1rem' }}>
                                {saving ? <Loader2 className="icon-spin" /> : <Save />}
                                {saving ? 'Wysyłanie...' : 'Wyślij do weryfikacji'}
                            </StyledButton>
                        )}
                    </FullWidthField>
                </FormGrid>
            </Card>
        </PageContentWrapper>
    );
};

export default PsychologistProfileEditor;